import React from 'react';
import { Box, Button, styled, Typography, Container } from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import headerImg from '../assets/header_img.png';

const Header = () => {
    const HeroSection = styled(Box)(({ theme }) => ({
        minHeight: '90vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 50%, #FFA500 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '600px',
            height: '600px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite',
        },
        [theme.breakpoints.down('md')]: {
            minHeight: '70vh',
            textAlign: 'center',
        },
    }));

    const ContentWrapper = styled(Container)(({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(6),
        position: 'relative',
        zIndex: 1,
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
            gap: theme.spacing(4),
        },
    }));

    const TextContent = styled(Box)(({ theme }) => ({
        flex: 1,
        animation: 'slideInLeft 0.8s ease-out',
        [theme.breakpoints.down('md')]: {
            textAlign: 'center',
        },
    }));

    const ImageContent = styled(Box)(({ theme }) => ({
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        animation: 'slideInRight 0.8s ease-out',
        [theme.breakpoints.down('md')]: {
            width: '100%',
        },
    }));

    const MainHeading = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 800,
        fontSize: 'clamp(2.5rem, 5vw, 4rem)',
        color: '#fff',
        lineHeight: 1.2,
        marginBottom: theme.spacing(3),
        textShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        '& .highlight': {
            background: 'linear-gradient(135deg, #FFF 0%, #FFE5B4 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
        },
    }));

    const SubHeading = styled(Typography)(({ theme }) => ({
        fontSize: 'clamp(1rem, 2vw, 1.25rem)',
        color: 'rgba(255, 255, 255, 0.95)',
        lineHeight: 1.8,
        marginBottom: theme.spacing(4),
        maxWidth: '600px',
        textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        [theme.breakpoints.down('md')]: {
            marginLeft: 'auto',
            marginRight: 'auto',
        },
    }));

    const CTAButton = styled(Button)(({ theme }) => ({
        padding: '1rem 2.5rem',
        fontSize: '1rem',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '50px',
        background: '#fff',
        color: '#FF6B35',
        border: '2px solid transparent',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            background: 'transparent',
            color: '#fff',
            borderColor: '#fff',
            transform: 'translateY(-3px)',
            boxShadow: '0 15px 40px rgba(0, 0, 0, 0.3)',
        },
        '& .MuiButton-endIcon': {
            transition: 'transform 0.3s ease',
        },
        '&:hover .MuiButton-endIcon': {
            transform: 'translateX(5px)',
        },
    }));

    const HeroImage = styled('img')(({ theme }) => ({
        width: '100%',
        maxWidth: '600px',
        height: 'auto',
        filter: 'drop-shadow(0 20px 40px rgba(0, 0, 0, 0.3))',
        animation: 'float 3s ease-in-out infinite',
        [theme.breakpoints.down('md')]: {
            maxWidth: '400px',
        },
    }));

    const StatsBox = styled(Box)(({ theme }) => ({
        display: 'flex',
        gap: theme.spacing(4),
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: theme.spacing(2),
            alignItems: 'center',
        },
    }));

    const StatItem = styled(Box)({
        textAlign: 'left',
        '& .number': {
            fontSize: '2.5rem',
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1,
            textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        },
        '& .label': {
            fontSize: '0.9rem',
            color: 'rgba(255, 255, 255, 0.9)',
            marginTop: '0.5rem',
        },
    });

    return (
        <HeroSection component="header">
            <ContentWrapper maxWidth="lg">
                <TextContent>
                    <MainHeading variant="h1">
                        Professional <span className="highlight">Crane Rental</span> Service in Coimbatore
                    </MainHeading>

                    <SubHeading variant="body1">
                        We specialize in providing top-notch crane rental services tailored to meet the demands of your most challenging projects. Our fleet boasts advanced cranes with lifting capacities from 10 to 200 tons.
                    </SubHeading>

                    <CTAButton 
                        component={Link} 
                        to="/about"
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                    >
                        Explore Our Services
                    </CTAButton>

                    <StatsBox>
                        <StatItem>
                            <div className="number">200+</div>
                            <div className="label">Projects Completed</div>
                        </StatItem>
                        <StatItem>
                            <div className="number">50+</div>
                            <div className="label">Expert Operators</div>
                        </StatItem>
                        <StatItem>
                            <div className="number">24/7</div>
                            <div className="label">Service Available</div>
                        </StatItem>
                    </StatsBox>
                </TextContent>

                <ImageContent>
                    <HeroImage 
                        src={headerImg} 
                        alt="Professional crane service in Coimbatore - Heavy lifting equipment"
                    />
                </ImageContent>
            </ContentWrapper>
        </HeroSection>
    );
};

export default Header;